/**
 * TagManager — Inline CRUD for LPA question tags.
 *
 * Renders inside the Settings tab. Allows creating, editing, toggling,
 * and deleting tags with optional color assignment.
 */

import { useState } from 'react';
import { useLanguage } from '@/core/LanguageContext';
import { PRIMARY_LANGUAGE } from '@/shared/config/languages';
import { useTags, useCreateTag, useUpdateTag, useDeleteTag } from '../hooks/useTags';
import type { LpaTag } from '../types';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Switch } from '@/shared/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Plus, Pencil, Trash2, Check, X, Tag } from 'lucide-react';

// ---------------------------------------------------------------------------
// Color presets (matches CompetencyLibrary pattern)
// ---------------------------------------------------------------------------

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#22c55e', '#10b981', '#14b8a6',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
];

// ---------------------------------------------------------------------------
// Color Picker sub-component
// ---------------------------------------------------------------------------

function ColorPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (color: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {/* No-color option */}
      <button
        type="button"
        className={`w-7 h-7 rounded-md border-2 border-dashed transition-all flex items-center justify-center ${
          value === null
            ? 'ring-2 ring-offset-1 ring-primary scale-110 border-primary'
            : 'border-muted-foreground/30 hover:scale-105'
        }`}
        onClick={() => onChange(null)}
        title="No color"
      >
        <X className="h-3 w-3 text-muted-foreground" />
      </button>
      {PRESET_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          className={`w-7 h-7 rounded-md transition-all ${
            value === color
              ? 'ring-2 ring-offset-1 ring-primary scale-110'
              : 'hover:scale-105'
          }`}
          style={{ backgroundColor: color }}
          onClick={() => onChange(color)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline editor row
// ---------------------------------------------------------------------------

interface TagRowProps {
  tag: LpaTag;
  onStartEdit: (tag: LpaTag) => void;
  onDelete: (tag: LpaTag) => void;
  onToggleActive: (tag: LpaTag) => void;
}

function TagRow({ tag, onStartEdit, onDelete, onToggleActive }: TagRowProps) {
  const { t, getTranslation } = useLanguage();

  return (
    <div className="flex items-center gap-3 py-2.5 px-3 rounded-lg border bg-card hover:bg-accent/30 transition-colors group">
      {/* Color dot */}
      <div
        className="w-4 h-4 rounded-full flex-shrink-0 border"
        style={{
          backgroundColor: tag.color ?? 'transparent',
          borderColor: tag.color ?? 'hsl(var(--border))',
        }}
      />

      {/* Name */}
      <div className="flex-1 min-w-0">
        <span className={`text-sm font-medium ${!tag.is_active ? 'text-muted-foreground line-through' : ''}`}>
          {getTranslation(tag.name_translations, tag.name)}
        </span>
        {tag.name_translations && Object.keys(tag.name_translations).length > 0 && (
          <span className="text-xs text-muted-foreground ml-2">
            ({tag.name})
          </span>
        )}
      </div>

      {/* Active toggle */}
      <Switch
        checked={tag.is_active}
        onCheckedChange={() => onToggleActive(tag)}
        aria-label={t('mod_lpa.tags.toggleActive', 'Toggle active')}
      />

      {/* Actions */}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => onStartEdit(tag)}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={() => onDelete(tag)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline edit / create form
// ---------------------------------------------------------------------------

interface TagFormState {
  name: string;
  nameTranslations: Record<string, string>;
  color: string | null;
}

function TagForm({
  initial,
  onSave,
  onCancel,
  isSaving,
}: {
  initial: TagFormState;
  onSave: (state: TagFormState) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const { t, availableLanguages, secondaryLanguage } = useLanguage();
  const [state, setState] = useState<TagFormState>(initial);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.name.trim()) return;
    onSave(state);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3 rounded-lg border bg-accent/20">
      {/* Name fields */}
      <div className="space-y-2">
        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {t('mod_lpa.tags.nameEn', 'Name (English)')}
          </label>
          <Input
            value={state.name}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder={t('mod_lpa.tags.namePlaceholder', 'e.g. Safety, Quality, 5S...')}
            className="h-8 text-sm"
            autoFocus
          />
        </div>

        {/* Secondary language */}
        {secondaryLanguage && (
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t('mod_lpa.tags.nameSecondary', `Name (${availableLanguages.find(l => l.code === secondaryLanguage)?.name ?? secondaryLanguage})`)}
            </label>
            <Input
              value={state.nameTranslations[secondaryLanguage] ?? ''}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  nameTranslations: {
                    ...s.nameTranslations,
                    [secondaryLanguage]: e.target.value,
                  },
                }))
              }
              placeholder={t('mod_lpa.tags.nameSecondaryPlaceholder', 'Translation...')}
              className="h-8 text-sm"
            />
          </div>
        )}
      </div>

      {/* Color picker */}
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
          {t('mod_lpa.tags.color', 'Color')}
        </label>
        <ColorPicker value={state.color} onChange={(color) => setState((s) => ({ ...s, color }))} />
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!state.name.trim() || isSaving}>
          <Check className="h-3.5 w-3.5 mr-1" />
          {t('mod_lpa.tags.save', 'Save')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
          <X className="h-3.5 w-3.5 mr-1" />
          {t('mod_lpa.tags.cancel', 'Cancel')}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main TagManager
// ---------------------------------------------------------------------------

export default function TagManager() {
  const { t } = useLanguage();
  const { data: tags, isLoading } = useTags();
  const createTag = useCreateTag();
  const updateTag = useUpdateTag();
  const deleteTagMutation = useDeleteTag();

  const [mode, setMode] = useState<'idle' | 'creating' | 'editing'>('idle');
  const [editingTag, setEditingTag] = useState<LpaTag | null>(null);
  const [deletingTag, setDeletingTag] = useState<LpaTag | null>(null);

  const handleStartCreate = () => {
    setMode('creating');
    setEditingTag(null);
  };

  const handleStartEdit = (tag: LpaTag) => {
    setMode('editing');
    setEditingTag(tag);
  };

  const handleCancel = () => {
    setMode('idle');
    setEditingTag(null);
  };

  const handleSaveCreate = (state: TagFormState) => {
    createTag.mutate(
      {
        name: state.name.trim(),
        name_translations: state.nameTranslations,
        color: state.color,
      },
      { onSuccess: () => handleCancel() },
    );
  };

  const handleSaveEdit = (state: TagFormState) => {
    if (!editingTag) return;
    updateTag.mutate(
      {
        tagId: editingTag.id,
        input: {
          name: state.name.trim(),
          name_translations: state.nameTranslations,
          color: state.color,
        },
      },
      { onSuccess: () => handleCancel() },
    );
  };

  const handleToggleActive = (tag: LpaTag) => {
    updateTag.mutate({
      tagId: tag.id,
      input: { is_active: !tag.is_active },
    });
  };

  const handleConfirmDelete = () => {
    if (!deletingTag) return;
    deleteTagMutation.mutate(deletingTag.id, {
      onSuccess: () => setDeletingTag(null),
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('mod_lpa.tags.title', 'Question Tags')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const activeTags = (tags ?? []).filter((t) => t.is_active);
  const inactiveTags = (tags ?? []).filter((t) => !t.is_active);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              {t('mod_lpa.tags.title', 'Question Tags')}
            </CardTitle>
            <CardDescription>
              {t('mod_lpa.tags.description', 'Organize questions with color-coded tags. Tags are shared across all factories.')}
            </CardDescription>
          </div>
          {mode === 'idle' && (
            <Button size="sm" onClick={handleStartCreate}>
              <Plus className="h-4 w-4 mr-1" />
              {t('mod_lpa.tags.addTag', 'Add Tag')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Create form */}
          {mode === 'creating' && (
            <TagForm
              initial={{ name: '', nameTranslations: {}, color: null }}
              onSave={handleSaveCreate}
              onCancel={handleCancel}
              isSaving={createTag.isPending}
            />
          )}

          {/* Tag list */}
          {(tags ?? []).length === 0 && mode !== 'creating' ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Tag className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                {t('mod_lpa.tags.empty', 'No tags yet. Create your first tag to organize questions.')}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {/* Active tags */}
              {activeTags.map((tag) =>
                mode === 'editing' && editingTag?.id === tag.id ? (
                  <TagForm
                    key={tag.id}
                    initial={{
                      name: tag.name,
                      nameTranslations: tag.name_translations ?? {},
                      color: tag.color,
                    }}
                    onSave={handleSaveEdit}
                    onCancel={handleCancel}
                    isSaving={updateTag.isPending}
                  />
                ) : (
                  <TagRow
                    key={tag.id}
                    tag={tag}
                    onStartEdit={handleStartEdit}
                    onDelete={setDeletingTag}
                    onToggleActive={handleToggleActive}
                  />
                ),
              )}

              {/* Inactive section */}
              {inactiveTags.length > 0 && (
                <>
                  <div className="text-xs font-medium text-muted-foreground pt-3 pb-1">
                    {t('mod_lpa.tags.inactive', 'Inactive')}
                  </div>
                  {inactiveTags.map((tag) =>
                    mode === 'editing' && editingTag?.id === tag.id ? (
                      <TagForm
                        key={tag.id}
                        initial={{
                          name: tag.name,
                          nameTranslations: tag.name_translations ?? {},
                          color: tag.color,
                        }}
                        onSave={handleSaveEdit}
                        onCancel={handleCancel}
                        isSaving={updateTag.isPending}
                      />
                    ) : (
                      <TagRow
                        key={tag.id}
                        tag={tag}
                        onStartEdit={handleStartEdit}
                        onDelete={setDeletingTag}
                        onToggleActive={handleToggleActive}
                      />
                    ),
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingTag} onOpenChange={() => setDeletingTag(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('mod_lpa.tags.deleteTitle', 'Delete tag?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'mod_lpa.tags.deleteDescription',
                'This will remove the tag and unassign it from all questions. This action cannot be undone.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('mod_lpa.tags.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('mod_lpa.tags.confirmDelete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
